import { useEffect, useState } from 'react';

import { CONSENT_CHANGED_EVENT } from '../../utils/cookieConsent';

// Toolbars collapsing move the numbers by a few px; only a keyboard is this tall
const KEYBOARD_MIN_PX = 80;

/**
 * How much of the layout viewport the on-screen keyboard covers, from `visualViewport` (0 without a keyboard or the
 * API). iOS keeps the layout viewport's size with the keyboard up, so a sheet fixed to the bottom sits under it.
 */
export const keyboardInset = (win = window) => {
  const view = win.visualViewport;
  if (!view) return 0;
  const covered = win.innerHeight - view.height - view.offsetTop;
  return covered > KEYBOARD_MIN_PX ? Math.round(covered) : 0;
};

const sameView = (a, b) => a.inset === b.inset && a.height === b.height;
const CLOSED = { inset: 0, height: null };

/**
 * The keyboard's inset and the height the visitor actually sees, while `active` (the sheet is open on a phone).
 * Android browsers that shrink the page for the keyboard show up as a short height instead of an inset.
 */
export const useVisibleViewport = (active) => {
  const [view, setView] = useState(CLOSED);

  useEffect(() => {
    if (!active) {
      setView((previous) => (sameView(previous, CLOSED) ? previous : CLOSED));
      return undefined;
    }
    const visual = window.visualViewport;
    const update = () => {
      const next = { inset: keyboardInset(), height: Math.round(visual ? visual.height : window.innerHeight) };
      setView((previous) => (sameView(previous, next) ? previous : next));
    };
    update();
    visual?.addEventListener('resize', update);
    visual?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      visual?.removeEventListener('resize', update);
      visual?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [active]);

  return view;
};

/**
 * The cookie card's height while it waits for an answer (0 once it's answered or gone). It floats above everything,
 * this window included, so the window keeps that room free instead of letting the card cover its button.
 */
export const useCookieCardRoom = (active) => {
  const [room, setRoom] = useState(0);

  useEffect(() => {
    if (!active) {
      setRoom(0);
      return undefined;
    }
    const measure = () => {
      const card = document.querySelector('.mt-cookies');
      setRoom(card ? Math.ceil(card.getBoundingClientRect().height) : 0);
    };
    // Answering the card removes it after this event's handlers run
    const measureLater = () => window.setTimeout(measure, 0);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener(CONSENT_CHANGED_EVENT, measureLater);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener(CONSENT_CHANGED_EVENT, measureLater);
    };
  }, [active]);

  return room;
};
